import { localeDate } from "~/util/date/intl_dates";
import { addMonths, toLocalDate } from "~/util/date";
import { DASH } from "~/util/html/codepoint";

const MONTHS_PER_YEAR = 12;
const DAYS_PER_MONTH = 365.25 / MONTHS_PER_YEAR;

/** "1 claim" / "862 claims", with the count locale-formatted. */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count.toLocaleString()} ${count === 1 ? singular : plural}`;
}

/** A list of alternatives as a reader would say it: "Skyline", "Skyline or Old Town",
 *  "Skyline, Old Town, or Lawson". */
export function formatAlternatives(values: string[]): string {
  return new Intl.ListFormat("en", { type: "disjunction" }).format(values);
}

/** A list of things that all hold: "Skyline", "Skyline and Old Town",
 *  "Skyline, Old Town, and Lawson". */
export function formatList(values: string[]): string {
  return new Intl.ListFormat("en", { type: "conjunction" }).format(values);
}

/** Uppercases the first character, leaving the rest unchanged. */
export function capitalizeFirstLetter(str: string): string {
  if (!str) {
    return str;
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
}

/** Enum value to display label: "prefer_not_to_say" -> "Prefer Not To Say". */
/** "not_yet_checked" → "Not yet checked" */
export function humanizeStatus(status: string): string {
  return capitalizeFirstLetter(status.replace(/_/g, " "));
}

export function titleize(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function rawFormatDate(
  date: Date | string | null,
  fallback: string,
  options: Intl.DateTimeFormatOptions = {},
  locale = "en-US",
): string {
  if (!date) {
    return fallback;
  }
  if (typeof date === "string") {
    if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // date gets interpreted as utc when in iso format
      options.timeZone = "UTC";
    }

    date = new Date(date);
  }

  return localeDate(locale, date, options);
}

// A date in another language's own format ("4/11/2026" in Spanish), for patient-facing text.
export function formatDateIn(
  date: Date | string,
  locale: string,
  options: Intl.DateTimeFormatOptions = {},
): string {
  return rawFormatDate(date, "", { ...options }, locale);
}

// Example: Jan 5, 2026
// `timezone` is for timestamps whose calendar date must match what the rest of the page says
// about the same instant; date-only strings already render as themselves.
export function formatLongDate(
  date: Date | string | null,
  fallback = "",
  timezone?: string,
): string {
  return rawFormatDate(date, fallback, {
    ...(timezone ? { timeZone: timezone } : {}),
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Example: Thu, Jan 8, 2026
export function formatLongDateWithWeekday(
  date: Date | string | null,
  fallback = "",
  timezone?: string,
): string {
  return rawFormatDate(date, fallback, {
    ...(timezone ? { timeZone: timezone } : {}),
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Example: Jan 5
// `timezone` is for timestamps whose calendar date must match what the rest of the page says
// about the same instant; date-only strings already render as themselves.
export function formatShortDate(
  date: Date | string | null,
  fallback = "",
  timezone?: string,
): string {
  return rawFormatDate(date, fallback, {
    ...(timezone ? { timeZone: timezone } : {}),
    month: "short",
    day: "numeric",
  });
}

// Example: Jan 5 this year, Jan 5, 2025 in any other, for a date that is usually recent. `timezone`
// places both the date and "this year" on one office's calendar.
export function formatShortDateWithOtherYear(
  date: Date | string | null,
  fallback = "",
  timezone?: string,
): string {
  if (!date) {
    return fallback;
  }
  const year = (value: Date | string) =>
    rawFormatDate(value, "", { ...(timezone ? { timeZone: timezone } : {}), year: "numeric" });

  return year(date) === year(new Date())
    ? formatShortDate(date, fallback, timezone)
    : formatLongDate(date, fallback, timezone);
}

/** Example: Jan */
export function formatShortMonth(date: Date | string | null, fallback = ""): string {
  return rawFormatDate(date, fallback, { month: "short" });
}

/** Example: Jan 2026 */
export function formatMonthYear(date: Date | string | null, fallback = ""): string {
  return rawFormatDate(date, fallback, {
    month: "short",
    year: "numeric",
  });
}

/** Example: January 2026 */
export function formatLongMonthYear(
  date: Date | string | null,
  fallback = "",
  timezone?: string,
): string {
  return rawFormatDate(date, fallback, {
    ...(timezone ? { timeZone: timezone } : {}),
    month: "long",
    year: "numeric",
  });
}

/** Example: 5 */
export function formatDay(date: Date | string | null, fallback = ""): string {
  return rawFormatDate(date, fallback, { day: "numeric" });
}

export function formatWeekdayDayMonthDate(date: Date | string | null, fallback = ""): string {
  return rawFormatDate(date, fallback, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function formatTime(time: Date | string, timezone?: string) {
  time = typeof time === "string" ? new Date(time) : time;
  return time.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
  });
}

/**
 * The practice-local date of service. `date` arrives as a bare ISO date already in the location's
 * timezone (bare dates render pinned to UTC, so every viewer reads the same day); `startTime` is a
 * last-resort fallback for rows missing the derived date, and renders viewer-local.
 */
export function formatAppointmentDate(
  appointment: { date: string | null; startTime: string | null },
  fallback = "",
): string {
  return formatLongDate(appointment.date ?? appointment.startTime, fallback);
}

export function formatAppointmentTimeSpan(
  appointment: {
    endTime: string | null;
    startTime: string | null;
  },
  fallback = "",
  timezone?: string,
): string {
  const { startTime, endTime } = appointment;
  if (startTime && endTime) {
    return `${formatTime(startTime, timezone)} - ${formatTime(endTime, timezone)}`;
  } else if (startTime) {
    return formatTime(startTime, timezone);
  }
  return fallback;
}

// Example: 1/5/2026 (default), 01/05/2026 (padded)
// `timezone` is for timestamps whose calendar date must match what the rest of the page says
// about the same instant; date-only strings already render as themselves.
export function formatDate(
  date: Date | string | null,
  fallback = "",
  { padded = false, timezone }: { padded?: boolean; timezone?: string } = {},
): string {
  return rawFormatDate(date, fallback, {
    ...(timezone ? { timeZone: timezone } : {}),
    ...(padded ? { year: "numeric", month: "2-digit", day: "2-digit" } : {}),
  });
}

// Example: Monday, 1/5/2026
export function formatDateWithWeekday(date: Date | string | null, fallback = ""): string {
  return rawFormatDate(date, fallback, {
    weekday: "long",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

export function formatDayMonth(date: Date | string | null, fallback = ""): string {
  return rawFormatDate(date, fallback, {
    month: "short",
    day: "numeric",
  });
}

// Chart bucket labels for UTC-bucketed series. Example: Jul 19 (day), Jul 19, 2 PM (hour)
export function formatUtcChartBucket(
  date: Date | string | null,
  interval: "hour" | "day",
  fallback = "",
): string {
  return rawFormatDate(date, fallback, {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    ...(interval === "hour" ? { hour: "numeric" } : {}),
  });
}

export function formatDateTime(
  date: Date | string | null,
  fallback = "",
  timezone?: string,
): string {
  return rawFormatDate(date, fallback, {
    ...(timezone ? { timeZone: timezone } : {}),
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

// Example: Apr 4, 2026 at 3:29 PM — the JS twin of Rails' :day_at_time (config/locales/en.yml).
// Composed by hand because Intl only joins with " at " once the month is spelled out.
export function formatDayAtTime(
  date: Date | string | null,
  fallback = "",
  timezone?: string,
): string {
  if (!date) {
    return fallback;
  }

  const day = rawFormatDate(date, "", {
    ...(timezone ? { timeZone: timezone } : {}),
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return day ? `${day} at ${formatTime(date, timezone)}` : fallback;
}

// Example: 04/04/2026, 3:29 PM
export function formatNumericDateTime(
  date: Date | string | null,
  fallback = "",
  timezone?: string,
): string {
  return rawFormatDate(date, fallback, {
    ...(timezone ? { timeZone: timezone } : {}),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatDateTimeWithSeconds(date: Date | string | null, fallback = ""): string {
  return rawFormatDate(date, fallback, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function timeAgo(date: Date | string | null, now: Date = new Date()): string {
  if (!date) return "";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  const diffMs = now.getTime() - dateObj.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function overUnlessExact(distance: string, onAnniversary: boolean): string {
  return onAnniversary ? distance : `over ${distance}`;
}

/**
 * How far one calendar date sits before another: whole months floored, with
 * "over" wherever any days are left over ("over 6 months"), and the bare count
 * on an exact anniversary ("6 months"). Months carry to just under two years
 * ("over 18 months") because payer thresholds sit at twelve, twenty-four and
 * thirty-six months, so a month is the unit a reader is deciding in; past that
 * it is whole years the same way ("2 years", "over 5 years").
 *
 * Floor and never round, because this line sits under a strict boundary: a
 * record shown below an "Over 6 months" divider that rounded up to "7 months"
 * reads as a contradiction, and rounding down would overstate how recent it is.
 *
 * Both ends are read as calendar dates and nothing else — a timestamp is taken
 * by its date part — so a browser an hour off the practice cannot move either
 * end by a day. A date on or after the reference has no distance to name and
 * reads as "0 months".
 */
export function formatDateDistance(date: string, reference: string): string {
  const earlier = toLocalDate(date.slice(0, 10));
  const later = toLocalDate(reference.slice(0, 10));

  if (later <= earlier) return pluralize(0, "month");

  const calendarMonths =
    (later.getFullYear() - earlier.getFullYear()) * MONTHS_PER_YEAR +
    (later.getMonth() - earlier.getMonth());
  const months = addMonths(earlier, calendarMonths) <= later ? calendarMonths : calendarMonths - 1;
  const onAnniversary = addMonths(earlier, months).getTime() === later.getTime();

  if (months < MONTHS_PER_YEAR * 2)
    return overUnlessExact(pluralize(months, "month"), onAnniversary);

  const years = Math.floor(months / MONTHS_PER_YEAR);
  return overUnlessExact(pluralize(years, "year"), onAnniversary && months % MONTHS_PER_YEAR === 0);
}

/** Returns relative time until a future date (e.g., "14 days", "1 day", "today", "passed") */
export function daysUntil(date: Date | string | null): number {
  if (!date) return 0;

  let target: Date;
  if (typeof date === "string") {
    if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Date-only string (YYYY-MM-DD) - parse as local date
      const [year, month, day] = date.split("-").map(Number);
      target = new Date(year!, month! - 1, day);
    } else {
      target = new Date(date);
    }
  } else {
    target = new Date(date);
  }

  const now = new Date();
  now.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  const diffMs = target.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  return diffDays;
}

/** A future day count the way staff say it: "tomorrow", "in 9 days", "in 3 weeks", "in 4 months".
 *  Exact days under two weeks, where next week vs the one after matters; past that it rounds,
 *  since it sits beside the date itself. */
export function formatDaysAway(days: number): string {
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  if (days < 14) return `in ${pluralize(days, "day")}`;
  if (days < 56) return `in ${pluralize(Math.round(days / 7), "week")}`;
  return `in ${pluralize(Math.round(days / DAYS_PER_MONTH), "month")}`;
}

export function formatDuration(
  seconds: number | null,
  options: { fallback?: string; secondsDecimals?: number } = {},
): string {
  const { fallback = "", secondsDecimals } = options;
  if (seconds === null) return fallback;
  // Round to the requested precision *before* splitting into minutes/seconds so the
  // seconds field never rolls over to 60 (e.g. 119.5s -> "2m 0s", not "1m 60s").
  const factor = secondsDecimals !== undefined ? 10 ** secondsDecimals : 1;
  const rounded = Math.round(seconds * factor) / factor;
  const mins = Math.floor(rounded / 60);
  const secs = rounded - mins * 60;
  const secsStr = secondsDecimals !== undefined ? secs.toFixed(secondsDecimals) : String(secs);
  if (mins === 0) return `${secsStr}s`;
  return `${mins}m ${secsStr}s`;
}

// A duration in clock style, "m:ss" (e.g. "1:09") — for live timers / media scrubbers. (formatDuration
// is the prose form, "1m 9s".) Non-finite or negative → "0:00". `padMinutes` gives "01:09", so a
// counter ticking in place does not change width as it crosses ten minutes.
export function formatClockDuration(seconds: number, options?: { padMinutes?: boolean }): string {
  const pad = (value: number) => value.toString().padStart(2, "0");
  if (!Number.isFinite(seconds) || seconds < 0) return options?.padMinutes ? "00:00" : "0:00";

  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  return `${options?.padMinutes ? pad(minutes) : minutes}:${pad(total % 60)}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatPhoneNumber(number: string | null, fallback = ""): string {
  if (!number) {
    return fallback;
  }
  const match = number.replace(/\D/g, "").match(/^1?(\d{3})(\d{3})(\d{4})$/);
  return match ? `(${match[1]}) ${match[2]}-${match[3]}` : number;
}

/** Abbreviated dollars from cents ($450, $31.5k). */
export function formatCents(cents: number): string {
  return formatUSD(cents, { fromCents: true, style: "abbreviated" });
}

/** Dollars from cents, or the dash where there is no figure. */
export function formatUSDOrDash(cents: number | null | undefined): string {
  return cents == null ? DASH : formatUSD(cents, { fromCents: true });
}

/** A ratio as a whole percent, or the dash where there is nothing to divide. */
export function formatPace(ratio: number | null): string {
  if (ratio == null) return DASH;
  return `${Math.round(ratio * 100)}%`;
}

export function formatUSD(
  value: string | number,
  options?: {
    fromCents?: boolean;
    style?: "cents" | "dollars" | "abbreviated" | "compact" | "precise";
  },
): string {
  const numericValue = typeof value === "string" ? parseFloat(value) : value;
  const dollarValue = options?.fromCents ? numericValue / 100.0 : numericValue;
  const absValue = Math.abs(dollarValue);
  const sign = numericValue < 0 ? "-" : "";
  const style = options?.style ?? "cents";

  // "compact" carries K/M/B; "abbreviated" only knows k, so it renders $1.2M as $1200k.
  if (style === "compact") {
    return `${sign}${new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 0,
    }).format(absValue)}`;
  }

  if (style === "abbreviated" && absValue >= 1000) {
    const abbreviated = absValue / 1000;
    const formatted = abbreviated % 1 === 0 ? abbreviated.toString() : abbreviated.toFixed(1);
    return `${sign}$${formatted}k`;
  }

  const minimumFractionDigits = style === "cents" || style === "precise" ? 2 : 0;
  const maximumFractionDigits = style === "precise" ? 6 : minimumFractionDigits;
  return `${sign}$${absValue.toLocaleString("en-US", {
    minimumFractionDigits,
    maximumFractionDigits,
  })}`;
}

/** `2026-08-01` or `2026-08` → `Aug 26`. */
export function formatMonthLabel(period: string): string {
  const [year, month] = period.split("-").map(Number);
  return new Date(year!, month! - 1, 1).toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
  });
}

export function formatDisplayName(nameParts: {
  firstName: string | null;
  lastName: string | null;
  preferredName?: string | null;
}): string {
  const { firstName, lastName, preferredName } = nameParts;

  if (!firstName && !lastName) {
    return "";
  }

  const parts = [];
  if (firstName) {
    parts.push(firstName);
  }
  if (preferredName) {
    parts.push(`(${preferredName})`);
  }
  if (lastName) {
    parts.push(lastName);
  }
  return parts.join(" ");
}

type Provider = {
  firstName: string | null;
  lastName: string | null;
  groupName?: string | null;
  type: "hygienist" | "dentist" | "group";
};

export function formatProviderName(
  provider: Provider,
  options?: {
    style: "short" | "long";
  },
): string {
  const { firstName, lastName, groupName, type } = provider;

  if (groupName) {
    return groupName;
  }

  if (!firstName && !lastName) {
    return DASH;
  }

  const isHygienist = type === "hygienist";
  const style = options?.style || "short";

  if (style === "short") {
    if (isHygienist) {
      return [firstName, lastName].filter(Boolean).join(" ") || "";
    } else {
      return `Dr. ${lastName || ""}`;
    }
  } else {
    const fullName = formatDisplayName({ firstName, lastName });
    if (isHygienist) {
      return fullName;
    } else {
      return `Dr. ${fullName}`;
    }
  }
}

const PROVIDER_PRIORITY = {
  dentist: 0,
  hygienist: 1,
  group: 2,
};

function getProviderSortName(provider: Provider): string {
  if (provider.groupName) {
    return provider.groupName;
  }
  if (provider.type === "dentist") {
    return provider.lastName!;
  }
  return [provider.firstName, provider.lastName].filter(Boolean).join(" ");
}

export function sortProviders(providerA: Provider | null, providerB: Provider | null): number {
  if (!providerA && !providerB) {
    return 0;
  }
  if (!providerA) {
    return 1;
  }
  if (!providerB) {
    return -1;
  }

  const priorityA = PROVIDER_PRIORITY[providerA.groupName ? "group" : providerA.type];
  const priorityB = PROVIDER_PRIORITY[providerB.groupName ? "group" : providerB.type];

  const byProviderType = priorityA - priorityB;
  if (byProviderType !== 0) {
    return byProviderType;
  }

  const nameA = getProviderSortName(providerA);
  const nameB = getProviderSortName(providerB);
  return nameA.localeCompare(nameB);
}

/** Example: "PST" or "PDT" for "America/Los_Angeles" */
export function formatTimezoneAbbreviation(ianaTimezone: string): string {
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: ianaTimezone,
    timeZoneName: "short",
  }).formatToParts(new Date());

  return parts.find((p) => p.type === "timeZoneName")?.value ?? ianaTimezone;
}

/** Example: "Pacific Standard Time" or "Pacific Daylight Time" for "America/Los_Angeles" */
export function formatTimezone(ianaTimezone: string): string {
  const parts = new Intl.DateTimeFormat("en", {
    timeZone: ianaTimezone,
    timeZoneName: "long",
  }).formatToParts(new Date());

  return parts.find((p) => p.type === "timeZoneName")?.value ?? ianaTimezone;
}
